// Phase 4 #1 — Recent Match Results card for the Command Center.
// Pulls the most-recent N matches that have a recorded result, with
// score + outcome + top performers highlighted. Hides itself entirely
// if no results have been entered yet (graceful for fresh installs).

import React, { useEffect, useMemo, useState } from "react";
import {
  Trophy, Award, Hand, Sparkles, Shield
} from "lucide-react";
import { api } from "../api";

const OUTCOME_CLASS = {
  WIN:       "result-row--win",
  LOSS:      "result-row--loss",
  DRAW:      "result-row--draw",
  TIE:       "result-row--tie",
  ABANDONED: "result-row--abandoned"
};
const OUTCOME_LABEL = {
  WIN: "WON", LOSS: "LOST", DRAW: "DRAW", TIE: "TIE", ABANDONED: "ABANDONED"
};

function fmtMatchDay(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric"
  });
}

function groupHighlights(highlights) {
  const out = { BEST_BATTER: [], BEST_BOWLER: [], TOP_PERFORMER: [] };
  for (const h of highlights || []) {
    if (out[h.category]) out[h.category].push(h);
  }
  return out;
}

export default function RecentResultsCard({ limit = 4, activeTeamId, onlyActiveTeam = false }) {
  const [state, setState] = useState({ status: "loading", rows: [] });

  useEffect(() => {
    let cancelled = false;
    api.listRecentResults({ limit: Math.max(limit, onlyActiveTeam ? limit * 3 : limit) })
      .then(rows => { if (!cancelled) setState({ status: "ok", rows: rows || [] }); })
      .catch(() => { if (!cancelled) setState({ status: "err", rows: [] }); });
    return () => { cancelled = true; };
  }, [limit, onlyActiveTeam]);

  const rows = useMemo(() => {
    if (state.status !== "ok") return [];
    const filtered = onlyActiveTeam && activeTeamId
      ? state.rows.filter(r => r.teamId === activeTeamId)
      : state.rows;
    return filtered.slice(0, limit);
  }, [state, onlyActiveTeam, activeTeamId, limit]);

  if (state.status !== "ok") return null;
  if (rows.length === 0)     return null;

  return (
    <div className="recentResults">
      <header className="recentResults-head">
        <Trophy size={14}/>
        <span>Recent Results</span>
      </header>
      <ul className="recentResults-list">
        {rows.map(r => {
          const hl = groupHighlights(r.highlights);
          const cricket = r.sport === "Cricket";
          return (
            <li key={r.matchId} className={`result-row ${OUTCOME_CLASS[r.outcome] || ""}`}>
              <div className="result-row-head">
                <span className={`result-row-badge result-row-badge--${(r.outcome || "").toLowerCase()}`}>
                  {OUTCOME_LABEL[r.outcome] || r.outcome}
                </span>
                <div className="result-row-vs">
                  <b>
                    {(r.teamName || "Team").replace(/(Cricket|Volleyball|Team|Club)/gi, "").trim()}
                  </b>
                  <em>vs</em>
                  <b>{r.opponent}</b>
                </div>
                <span className="result-row-score">
                  {r.homeScore || "—"}
                  <small> · </small>
                  {r.awayScore || "—"}
                </span>
              </div>
              <div className="result-row-meta">
                <span><Shield size={11}/> {r.sport}</span>
                <span>{fmtMatchDay(r.matchAt)}</span>
                {r.venue && <span>· {r.venue}</span>}
              </div>

              {/* Top performers — sport-aware. */}
              {(hl.TOP_PERFORMER.length + hl.BEST_BATTER.length + hl.BEST_BOWLER.length) > 0 && (
                <div className="result-row-stars">
                  {hl.TOP_PERFORMER.map(h => (
                    <span key={`tp-${h.userId}`} className="star-chip star-chip--gold">
                      <Award size={11}/> {h.userName}
                      <small>Top performer</small>
                    </span>
                  ))}
                  {cricket && hl.BEST_BATTER.map(h => (
                    <span key={`bat-${h.userId}`} className="star-chip star-chip--orange">
                      <Trophy size={11}/> {h.userName}
                      <small>Best batter{h.notes ? ` · ${h.notes}` : ""}</small>
                    </span>
                  ))}
                  {cricket && hl.BEST_BOWLER.map(h => (
                    <span key={`bowl-${h.userId}`} className="star-chip star-chip--blue">
                      <Hand size={11}/> {h.userName}
                      <small>Best bowler{h.notes ? ` · ${h.notes}` : ""}</small>
                    </span>
                  ))}
                </div>
              )}

              {r.notes && <div className="result-row-notes">{r.notes}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
